/**
 * Validation et nettoyage d'un logo client, SANS décodage de pixels ni API navigateur (mêmes
 * résultats sur le poste et sur le serveur) :
 *  - le type réel est déterminé par les octets magiques (PNG/JPEG uniquement ; SVG et tout le reste refusés) ;
 *  - le fichier est RÉÉCRIT à partir de ses seuls segments utiles : métadonnées (EXIF, GPS, texte,
 *    profils ICC, miniatures, commentaires) supprimées ;
 *  - toute donnée après la fin d'image (polyglotte), tout marqueur HTML/SVG/script, toute structure
 *    corrompue (CRC PNG, segments JPEG tronqués) est refusé ;
 *  - dimensions, ratio et poids bornés.
 */

export const LOGO_MAX_BYTES = 512 * 1024;
/**
 * Plafond en Hybride : la synchronisation limite une fiche à 256 Kio et le logo (base64, +33 %) voyage dans
 * le profil entreprise ; 160 Ko laissent la marge pour le reste du profil et les textes de la charte.
 */
export const LOGO_MAX_BYTES_SYNCED = 160 * 1024;
export const LOGO_MAX_DIMENSION = 1024;
export const LOGO_MIN_DIMENSION = 16;
export const LOGO_MAX_RATIO = 5;

export type LogoRejectionCode =
  | 'empty'
  | 'too_large'
  | 'svg'
  | 'unsupported_type'
  | 'polyglot'
  | 'corrupt'
  | 'dimensions'
  | 'ratio'
  | 'orientation';

const MESSAGES: Record<LogoRejectionCode, string> = {
  empty: 'Fichier vide.',
  too_large: 'Logo trop volumineux : 512 Ko maximum.',
  svg: 'Les logos SVG sont refusés (risque de sécurité) : utilisez un PNG ou un JPEG.',
  unsupported_type: 'Format non pris en charge : utilisez un PNG ou un JPEG.',
  polyglot:
    'Fichier refusé : il contient des données cachées après l’image ou du code (fichier polyglotte).',
  corrupt: 'Image illisible ou corrompue : réexportez-la depuis votre logiciel graphique.',
  dimensions: `Dimensions refusées : ${String(LOGO_MIN_DIMENSION)} à ${String(LOGO_MAX_DIMENSION)} pixels par côté.`,
  ratio: `Proportions refusées : le rapport largeur/hauteur doit rester entre 1:${String(LOGO_MAX_RATIO)} et ${String(LOGO_MAX_RATIO)}:1.`,
  orientation:
    'Cette photo contient une rotation EXIF : réexportez le logo à l’endroit (sans rotation) avant de le téléverser.',
};

export class LogoRejectedError extends Error {
  readonly code: LogoRejectionCode;
  constructor(code: LogoRejectionCode) {
    super(MESSAGES[code]);
    this.name = 'LogoRejectedError';
    this.code = code;
  }
}

export type LogoMime = 'image/png' | 'image/jpeg';

export interface SanitizedLogo {
  readonly mime: LogoMime;
  readonly bytes: Uint8Array;
  readonly width: number;
  readonly height: number;
}

const PNG_SIG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;

function startsWith(bytes: Uint8Array, sig: readonly number[]): boolean {
  return bytes.length >= sig.length && sig.every((b, i) => bytes[i] === b);
}

function asciiAt(bytes: Uint8Array, from: number, to: number): string {
  let out = '';
  for (let i = from; i < Math.min(to, bytes.length); i += 1)
    out += String.fromCharCode(bytes[i] ?? 0);
  return out;
}

/** `<?xml` en tête de fichier, après espaces et éventuelle marque d'ordre des octets (U+FEFF ou EF BB BF). */
function startsWithXmlProlog(head: string): boolean {
  let i = 0;
  for (;;) {
    const c = head.charCodeAt(i);
    const bom = c === 0xfeff || c === 0xef || c === 0xbb || c === 0xbf;
    if (!(bom || c <= 0x20)) break;
    i += 1;
  }
  return head.startsWith('<?xml', i);
}

export type SniffedType = 'png' | 'jpeg' | 'svg' | 'unknown';

/** Type RÉEL d'après les octets (jamais l'extension ni le type MIME déclaré). */
export function sniffImageType(bytes: Uint8Array): SniffedType {
  if (startsWith(bytes, PNG_SIG)) return 'png';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff)
    return 'jpeg';
  const head = asciiAt(bytes, 0, 2048).toLowerCase();
  if (head.includes('<svg') || startsWithXmlProlog(head)) return 'svg';
  return 'unknown';
}

/** Marqueurs de contenu actif cachés dans une image (polyglottes HTML/SVG/PHP/JS). */
const ACTIVE_CONTENT = [
  '<svg',
  '<script',
  '<html',
  '<iframe',
  '<?xml',
  '<?php',
  '<!doctype',
  'javascript:',
  'onerror=',
  'onload=',
] as const;

function containsActiveContent(bytes: Uint8Array): boolean {
  // Recherche insensible à la casse sur la représentation latin-1 de tout le fichier.
  let text = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    text += String.fromCharCode(...bytes.subarray(i, Math.min(bytes.length, i + chunk)));
  }
  const lower = text.toLowerCase();
  return ACTIVE_CONTENT.some((m) => lower.includes(m));
}

let crcTable: Uint32Array | null = null;
function crc32(bytes: Uint8Array, from: number, to: number): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
      let c = n;
      for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = from; i < to; i += 1) {
    crc = (crcTable[(crc ^ (bytes[i] ?? 0)) & 0xff] ?? 0) ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function u32(bytes: Uint8Array, at: number): number {
  return (
    (((bytes[at] ?? 0) << 24) |
      ((bytes[at + 1] ?? 0) << 16) |
      ((bytes[at + 2] ?? 0) << 8) |
      (bytes[at + 3] ?? 0)) >>>
    0
  );
}

function u16(bytes: Uint8Array, at: number): number {
  return ((bytes[at] ?? 0) << 8) | (bytes[at + 1] ?? 0);
}

function checkGeometry(width: number, height: number): void {
  if (
    width < LOGO_MIN_DIMENSION ||
    height < LOGO_MIN_DIMENSION ||
    width > LOGO_MAX_DIMENSION ||
    height > LOGO_MAX_DIMENSION
  ) {
    throw new LogoRejectedError('dimensions');
  }
  const ratio = width / height;
  if (ratio > LOGO_MAX_RATIO || ratio < 1 / LOGO_MAX_RATIO) throw new LogoRejectedError('ratio');
}

const PNG_KEPT = new Set(['IHDR', 'PLTE', 'tRNS', 'IDAT', 'IEND']);
const PNG_VALID_DEPTHS: Record<number, readonly number[]> = {
  0: [1, 2, 4, 8, 16],
  2: [8, 16],
  3: [1, 2, 4, 8],
  4: [8, 16],
  6: [8, 16],
};

function sanitizePng(input: Uint8Array): SanitizedLogo {
  const out: number[][] = [];
  let pos = 8;
  let width = 0;
  let height = 0;
  let colorType = -1;
  let seenIhdr = false;
  let seenPlte = false;
  let seenIdat = false;
  let idatClosed = false;
  let ended = false;

  while (pos < input.length) {
    if (pos + 12 > input.length) throw new LogoRejectedError('corrupt');
    const len = u32(input, pos);
    if (len > input.length - pos - 12) throw new LogoRejectedError('corrupt');
    const type = asciiAt(input, pos + 4, pos + 8);
    if (!/^[A-Za-z]{4}$/.test(type)) throw new LogoRejectedError('corrupt');
    const dataStart = pos + 8;
    const dataEnd = dataStart + len;
    if (crc32(input, pos + 4, dataEnd) !== u32(input, dataEnd))
      throw new LogoRejectedError('corrupt');

    if (!seenIhdr && type !== 'IHDR') throw new LogoRejectedError('corrupt');
    if (type === 'IHDR') {
      if (seenIhdr || len !== 13) throw new LogoRejectedError('corrupt');
      width = u32(input, dataStart);
      height = u32(input, dataStart + 4);
      const depth = input[dataStart + 8] ?? 0;
      colorType = input[dataStart + 9] ?? -1;
      const compression = input[dataStart + 10] ?? 0;
      const filter = input[dataStart + 11] ?? 0;
      const interlace = input[dataStart + 12] ?? 0;
      if (!(PNG_VALID_DEPTHS[colorType] ?? []).includes(depth))
        throw new LogoRejectedError('corrupt');
      if (compression !== 0 || filter !== 0 || interlace > 1)
        throw new LogoRejectedError('corrupt');
      checkGeometry(width, height);
      seenIhdr = true;
    } else if (type === 'PLTE') {
      if (seenIdat || seenPlte || len === 0 || len % 3 !== 0 || len > 768) {
        throw new LogoRejectedError('corrupt');
      }
      seenPlte = true;
    } else if (type === 'IDAT') {
      if (idatClosed || (colorType === 3 && !seenPlte)) throw new LogoRejectedError('corrupt');
      seenIdat = true;
    } else if (type === 'IEND') {
      if (len !== 0 || !seenIdat) throw new LogoRejectedError('corrupt');
      ended = true;
    }
    if (seenIdat && type !== 'IDAT') idatClosed = true;

    const critical = (type.charCodeAt(0) & 0x20) === 0;
    if (PNG_KEPT.has(type)) {
      out.push(Array.from(input.subarray(pos, dataEnd + 4)));
    } else if (critical) {
      throw new LogoRejectedError('corrupt');
    }
    // Chunks annexes (tEXt, iTXt, zTXt, eXIf, iCCP, pHYs, tIME, gAMA, acTL/fcTL/fdAT…) : supprimés.
    pos = dataEnd + 4;
    if (ended) break;
  }

  if (!ended) throw new LogoRejectedError('corrupt');
  if (pos !== input.length) throw new LogoRejectedError('polyglot');

  const body = out.flat();
  const bytes = new Uint8Array(PNG_SIG.length + body.length);
  bytes.set(PNG_SIG, 0);
  bytes.set(body, PNG_SIG.length);
  return { mime: 'image/png', bytes, width, height };
}

/** Orientation EXIF (tag 0x0112) d'un segment APP1, ou null si absente. */
function exifOrientation(input: Uint8Array, start: number, end: number): number | null {
  if (asciiAt(input, start, start + 4) !== 'Exif') return null;
  const tiff = start + 6;
  const little = asciiAt(input, tiff, tiff + 2) === 'II';
  const r16 = (at: number): number =>
    little ? (input[at] ?? 0) | ((input[at + 1] ?? 0) << 8) : u16(input, at);
  const r32 = (at: number): number =>
    little
      ? ((input[at] ?? 0) |
          ((input[at + 1] ?? 0) << 8) |
          ((input[at + 2] ?? 0) << 16) |
          ((input[at + 3] ?? 0) << 24)) >>>
        0
      : u32(input, at);
  if (tiff + 8 > end) return null;
  const ifd = tiff + r32(tiff + 4);
  if (ifd + 2 > end) return null;
  const count = Math.min(r16(ifd), 64);
  for (let i = 0; i < count; i += 1) {
    const entry = ifd + 2 + i * 12;
    if (entry + 12 > end) return null;
    if (r16(entry) === 0x0112) return r16(entry + 8);
  }
  return null;
}

function sanitizeJpeg(input: Uint8Array): SanitizedLogo {
  const kept: number[][] = [];
  let pos = 2;
  let width = 0;
  let height = 0;
  let seenSof = false;
  let seenSos = false;

  for (;;) {
    if (pos + 2 > input.length) throw new LogoRejectedError('corrupt');
    if (input[pos] !== 0xff) throw new LogoRejectedError('corrupt');
    let marker = input[pos + 1] ?? 0;
    while (marker === 0xff) {
      pos += 1;
      marker = input[pos + 1] ?? 0;
    }
    if (marker === 0xd9) {
      // EOI
      if (!seenSos) throw new LogoRejectedError('corrupt');
      pos += 2;
      break;
    }
    if (marker === 0x00 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      throw new LogoRejectedError('corrupt');
    }
    if (pos + 4 > input.length) throw new LogoRejectedError('corrupt');
    const len = u16(input, pos + 2);
    if (len < 2 || pos + 2 + len > input.length) throw new LogoRejectedError('corrupt');
    const segStart = pos + 4;
    const segEnd = pos + 2 + len;

    const isSof =
      marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isSof) {
      // Seuls baseline/étendu/progressif Huffman (SOF0-2) : pas de lossless ni d'arithmétique.
      if (marker > 0xc2) throw new LogoRejectedError('unsupported_type');
      if (seenSof || len < 8) throw new LogoRejectedError('corrupt');
      height = u16(input, segStart + 1);
      width = u16(input, segStart + 3);
      const components = input[segStart + 5] ?? 0;
      if (components !== 1 && components !== 3) throw new LogoRejectedError('unsupported_type');
      checkGeometry(width, height);
      seenSof = true;
      kept.push(Array.from(input.subarray(pos, segEnd)));
    } else if (marker === 0xdb || marker === 0xc4 || marker === 0xdd) {
      // DQT, DHT, DRI : nécessaires au décodage.
      kept.push(Array.from(input.subarray(pos, segEnd)));
    } else if (marker === 0xda) {
      // SOS + données entropiques jusqu'au prochain marqueur (hors RSTn / octet de bourrage).
      if (!seenSof) throw new LogoRejectedError('corrupt');
      seenSos = true;
      let scan = segEnd;
      for (;;) {
        if (scan >= input.length) throw new LogoRejectedError('corrupt');
        if (input[scan] === 0xff) {
          const next = input[scan + 1];
          if (next === undefined) throw new LogoRejectedError('corrupt');
          if (next === 0x00 || (next >= 0xd0 && next <= 0xd7) || next === 0xff) {
            scan += next === 0xff ? 1 : 2;
            continue;
          }
          break;
        }
        scan += 1;
      }
      kept.push(Array.from(input.subarray(pos, scan)));
      pos = scan;
      continue;
    } else if (marker === 0xe1) {
      const orientation = exifOrientation(input, segStart, segEnd);
      if (orientation !== null && orientation !== 1) throw new LogoRejectedError('orientation');
      // APPn / COM : supprimés (EXIF, GPS, XMP, ICC, miniatures, commentaires).
    }
    pos = segEnd;
  }

  if (pos !== input.length) throw new LogoRejectedError('polyglot');
  if (!seenSof) throw new LogoRejectedError('corrupt');

  const body = kept.flat();
  const bytes = new Uint8Array(2 + body.length + 2);
  bytes.set([0xff, 0xd8], 0);
  bytes.set(body, 2);
  bytes.set([0xff, 0xd9], 2 + body.length);
  return { mime: 'image/jpeg', bytes, width, height };
}

/**
 * @throws LogoRejectedError avec un code stable et un message en français affichable tel quel.
 */
export function sanitizeLogoBytes(
  input: Uint8Array,
  maxBytes: number = LOGO_MAX_BYTES,
): SanitizedLogo {
  if (input.length === 0) throw new LogoRejectedError('empty');
  if (input.length > maxBytes) throw new LogoRejectedError('too_large');
  const type = sniffImageType(input);
  if (type === 'svg') throw new LogoRejectedError('svg');
  if (type === 'unknown') throw new LogoRejectedError('unsupported_type');
  if (containsActiveContent(input)) throw new LogoRejectedError('polyglot');
  const result = type === 'png' ? sanitizePng(input) : sanitizeJpeg(input);
  if (result.bytes.length > maxBytes) throw new LogoRejectedError('too_large');
  return result;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] ?? 0;
    const b = bytes[i + 1] ?? 0;
    const c = bytes[i + 2] ?? 0;
    const n = (a << 16) | (b << 8) | c;
    out += B64.charAt((n >> 18) & 63) + B64.charAt((n >> 12) & 63);
    out += i + 1 < bytes.length ? B64.charAt((n >> 6) & 63) : '=';
    out += i + 2 < bytes.length ? B64.charAt(n & 63) : '=';
  }
  return out;
}

const B64_RE = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

/** Retourne null si la chaîne n'est pas du base64 strict. */
export function base64ToBytes(b64: string): Uint8Array | null {
  if (b64.length === 0 || !B64_RE.test(b64)) return null;
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  const out = new Uint8Array((b64.length / 4) * 3 - pad);
  let o = 0;
  for (let i = 0; i < b64.length; i += 4) {
    const n =
      (B64.indexOf(b64.charAt(i)) << 18) |
      (B64.indexOf(b64.charAt(i + 1)) << 12) |
      ((b64.charAt(i + 2) === '=' ? 0 : B64.indexOf(b64.charAt(i + 2))) << 6) |
      (b64.charAt(i + 3) === '=' ? 0 : B64.indexOf(b64.charAt(i + 3)));
    if (o < out.length) out[o++] = (n >> 16) & 255;
    if (o < out.length) out[o++] = (n >> 8) & 255;
    if (o < out.length) out[o++] = n & 255;
  }
  return out;
}
